import { DestroyRef, Injectable, InjectionToken, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthGmsClientConfig, AuthUser } from '@coin/shared/auth/util';
import { StorageService } from '@coin/shared/core/data-access';
import { StorageKey } from '@coin/shared/core/util-enums';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { AuthUserState } from './auth-user-state';

export const AuthGmsClientConfigService = new InjectionToken<AuthGmsClientConfig>('AuthGmsClientConfig');

@Injectable()
export class AuthService {
  private injector = inject(Injector);
  private storageService = inject(StorageService);
  private oauthService = inject(OAuthService);
  private destroyRef = inject(DestroyRef);

  private authConfig: AuthConfig;
  public isE2eTest$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = new BehaviorSubject<boolean>(false);
  public isAuthorized$ = new BehaviorSubject<boolean>(true);
  public idToken$ = new BehaviorSubject<string>('');

  private get router(): Router {
    return this.injector.get(Router);
  }

  // Resolved lazily: UserState injects GeneralUserService, which injects this service
  private get userState(): AuthUserState {
    return this.injector.get(AuthUserState, null);
  }

  public set emulation(emulationToken: string) {
    this.storageService.setAsync(StorageKey.EMULATION_TOKEN, emulationToken);
  }

  public async getUser(): Promise<AuthUser> {
    const user: string = await this.getIdToken();
    if (user) {
      return this.decodeToken(user);
    }
  }

  public async getEmulationDecoded(): Promise<AuthUser> {
    const emulatedUser = await this.getEmulation();
    if (emulatedUser) {
      return this.decodeToken(emulatedUser);
    }
  }

  public async getEmulation(): Promise<string> {
    return this.storageService.getAsync(StorageKey.EMULATION_TOKEN);
  }

  public getIdToken(): Promise<string> {
    return Promise.resolve(this.oauthService.getIdToken() || '');
  }

  public getAccessToken(): Promise<string> {
    return Promise.resolve(this.oauthService.getIdToken() || '');
  }

  public async getActiveUser(): Promise<AuthUser> {
    const decoded = await this.getEmulationDecoded();
    const user = await this.getUser();
    return decoded ? { ...decoded, gid: decoded.Gid } : user;
  }

  public async getIsEmulated(): Promise<boolean> {
    const emulation = await this.getEmulation();
    return !!emulation;
  }

  constructor() {
    const config = inject(AuthGmsClientConfigService);

    this.authConfig = {
      clientId: config.clientId,
      issuer: config.issuer,
      redirectUri: config.callbackUrl,
      responseType: 'code',
      scope: config.scopes,
      showDebugInformation: true,
      strictDiscoveryDocumentValidation: false,
      skipIssuerCheck: true,
      openUri: this.openLoginUri.bind(this),
      timeoutFactor: 1
    };
    this.listenOnOAuthEvents();
  }

  public overwriteConfig(authConfig: AuthConfig): void {
    this.authConfig = authConfig;
  }

  public openLoginUri(uri: string): void {
    window.location.href = uri;
  }

  /**
   * @param redirectPath where to redirect to after an successful login
   */
  public async login(redirectPath?: string): Promise<void> {
    await this.initOAuthService();

    try {
      await this.oauthService.initLoginFlow();
      if (redirectPath) {
        await this.storageService.setAsync(StorageKey.REDIRECT_URL, redirectPath);
      }
    } catch (e) {
      console.error(e);
    }
  }

  public async refreshToken(): Promise<void> {
    await this.initOAuthService();
    const response = await this.oauthService.refreshToken();
    if (response?.id_token) {
      this.idToken$.next(response.id_token);
    }
  }

  public logOff(): void {
    this.oauthService.logOut();
    this.userState?.resetUser();

    // redirect to root of page
    this.router.navigate(['/logout']);
  }

  public getRemainingTime(): number {
    if (this.isE2eTest$.value) {
      // Causes the application-header to display the emulated name without an access-token
      return 3600;
    }

    const expirationInMilliseconds = this.oauthService.getIdTokenExpiration() - Date.now();
    return expirationInMilliseconds / 1000;
  }

  private listenOnOAuthEvents(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.get('coin-testing') === 'e2e') {
      this.isE2eTest$.next(true);
      setTimeout(() => {
        if (this.storageService.get(StorageKey.E2E_AUTH_TOKEN)) {
          this.isAuthenticated$.next(true);
          this.emulation = this.storageService.get(StorageKey.E2E_AUTH_TOKEN);
        }
      }, 2000);
      return;
    }

    this.isAuthenticated$.next(this.oauthService.hasValidIdToken());
    if (this.isAuthenticated$.value) {
      this.idToken$.next(this.oauthService.getIdToken() || '');
    }

    this.oauthService.events
      .pipe(
        tap(() => this.isAuthenticated$.next(this.oauthService.hasValidIdToken())),
        filter(() => this.oauthService.hasValidIdToken()),
        tap(() => this.idToken$.next(this.oauthService.getIdToken() || ''))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public async handleLoginCallback(): Promise<void> {
    await this.initOAuthService();
    await this.oauthService.tryLoginCodeFlow();

    this.idToken$.next(this.oauthService.getIdToken() || '');
    const redirectUrl = await this.storageService.getAsync(StorageKey.REDIRECT_URL);
    const stateUrl = redirectUrl === 'undefined' ? '/' : redirectUrl || '/';
    this.storageService.remove(StorageKey.REDIRECT_URL);
    if (this.userState) {
      await firstValueFrom(this.userState.loadUser(), { defaultValue: undefined });
      await firstValueFrom(this.userState.loadLoggedInUser(), { defaultValue: undefined });
    }

    await this.router.navigateByUrl(stateUrl);
  }

  private async initOAuthService(): Promise<void> {
    this.oauthService.configure(this.authConfig);
    await this.oauthService.loadDiscoveryDocument();
  }

  private decodeToken(tokenValue: string): AuthUser {
    let decoded = '{}';

    if (tokenValue) {
      decoded = this.b64DecodeUnicode(tokenValue.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'));
    }

    return JSON.parse(decoded);
  }

  private b64DecodeUnicode(str: string): string {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(
      atob(str)
        .split('')
        .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
  }

  public clearEmulation(): void {
    this.storageService.remove(StorageKey.EMULATION_TOKEN);
  }
}
